/**
 * First Sale Memorandum — template-based generation.
 * No AI needed. Instant output from party names + values.
 */

export interface MemoTemplateInputs {
  importerName: string
  middlemanName: string
  factoryName: string
  /** Additional intermediary, if any */
  intermediaryName?: string
  productDescription: string
  countryOfOrigin: string
  /** e.g. "6302.60.0020" */
  htsCode?: string
  /** Duty rate as a percentage, e.g. 25 for 25% */
  dutyRate?: number
  /** Price importer pays the middleman per unit */
  middlemanUnitPrice?: number
  /** Price middleman pays the factory per unit */
  factoryUnitPrice?: number
  /** Optional intermediary price */
  intermediaryUnitPrice?: number
  attorney?: string
  caseNumber?: string
  poNumber?: string
}

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v)

const today = () =>
  new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date())

export function generateMemoFromTemplate(inputs: MemoTemplateInputs): string {
  const {
    importerName,
    middlemanName,
    factoryName,
    intermediaryName,
    productDescription,
    countryOfOrigin,
    htsCode,
    dutyRate,
    middlemanUnitPrice,
    factoryUnitPrice,
    intermediaryUnitPrice,
    attorney,
    caseNumber,
    poNumber,
  } = inputs

  const hasIntermediary = !!intermediaryName?.trim()
  const hasPricing =
    middlemanUnitPrice != null &&
    factoryUnitPrice != null &&
    dutyRate != null &&
    middlemanUnitPrice > 0 &&
    factoryUnitPrice > 0 &&
    dutyRate > 0

  const lines: string[] = []

  // ── Header ──────────────────────────────────────────────────────
  lines.push("# MEMORANDUM")
  lines.push("")
  lines.push(`**Date:** ${today()}`)
  if (caseNumber) lines.push(`**Case Reference:** ${caseNumber}`)
  if (poNumber) lines.push(`**Purchase Order:** ${poNumber}`)
  if (htsCode) lines.push(`**HTS Classification:** ${htsCode}`)
  if (attorney) lines.push(`**Prepared By:** ${attorney}`)
  lines.push("")

  // ── Introduction ────────────────────────────────────────────────
  lines.push(
    "This memorandum is intended to provide an understanding of the basic principles that support the \"first sale\" appraisement of merchandise shipped to customers in the United States for U.S. Customs duty purposes (\"CBP\")."
  )
  lines.push("")
  lines.push(
    "The first sale duty savings opportunity exists where there is a sale from a factory to a middleman/trading company and from the middleman/trading company to a U.S. customer. If we can show that when the goods were sold by the factory to the middleman, the goods are destined for the United States at a price which is acceptable to CBP, then duty can be paid on the factory price rather than the price charged by the middleman."
  )
  lines.push("")

  // ── Current Structure ───────────────────────────────────────────
  lines.push("## Current Structure")
  lines.push("")
  if (hasIntermediary) {
    lines.push(
      `Currently, **${importerName}** is the importer of record for shipments of ${productDescription}. **${middlemanName}**, located in ${countryOfOrigin}, is the exporter of record. ${importerName} places a purchase order with ${middlemanName}, and ${middlemanName} places a purchase order with **${intermediaryName}**, and ${intermediaryName} places a purchase order with **${factoryName}** (${countryOfOrigin}). ${factoryName} produces the ${productDescription} and ships to ${intermediaryName}, who then arranges export to the United States.`
    )
  } else {
    lines.push(
      `Currently, **${importerName}** is the importer of record for shipments of ${productDescription}. **${middlemanName}**, located in ${countryOfOrigin}, is the exporter of record. ${importerName} places a purchase order with ${middlemanName}, and ${middlemanName} places a purchase order with **${factoryName}** (${countryOfOrigin}). ${factoryName} produces the ${productDescription} and ships them for export to the United States.`
    )
  }
  lines.push("")

  // ── Transaction Value ───────────────────────────────────────────
  lines.push("## Transaction Value")
  lines.push("")
  lines.push(
    "Most merchandise imported into the United States is appraised under the \"Transaction Value\" basis of appraisement. \"Transaction Value\" is defined as the price paid or payable by the buyer to (or for the benefit of) the seller when merchandise is sold for exportation to the United States. In addition, where certain expenses are incurred by (or for the account of) the buyer, and they are not included in the invoice price, then dutiable value must be increased to take these additional costs into account. These costs relate to packing charges and any materials provided to the manufacturer free of charge."
  )
  lines.push("")
  lines.push(
    "In certain instances, there can be more than one \"sale for export to the United States,\" and court decisions permit the first sale (from the factory to a middleman) to be the basis for transaction value appraisement. This is the doctrine of the so-called \"first sale rule\" or \"middleman concept.\""
  )
  lines.push("")

  // ── First Sale Rule ─────────────────────────────────────────────
  lines.push("## First Sale Rule")
  lines.push("")
  lines.push(
    `The first sale rule applies to back-to-back sales. In order to declare a customs value at ${factoryName}'s price, rather than the price from ${middlemanName} to ${importerName}, the following two conditions must be met:`
  )
  lines.push("")
  lines.push(
    "1. The factory has to sell to the middleman at a fairly negotiated price based on genuine figures; and"
  )
  lines.push("")
  lines.push(
    "2. The goods must be clearly intended for the U.S. market at the time of the first sale."
  )
  lines.push("")

  // ── Pricing Example ─────────────────────────────────────────────
  if (hasPricing) {
    lines.push(
      `In order to illustrate the concept, we have outlined the transaction in which **${importerName}** buys from **${middlemanName}** who, in turn, places an order with ${hasIntermediary ? `**${intermediaryName}**, who then places an order with ` : ""}**${factoryName}**. ${importerName} has agreed to pay ${middlemanName} ${fmt(middlemanUnitPrice!)} (FOB price) per unit${hasIntermediary && intermediaryUnitPrice != null ? ` and ${middlemanName} places the production order with ${intermediaryName} for ${fmt(intermediaryUnitPrice)}, who places the production order with ${factoryName} for ${fmt(factoryUnitPrice!)}` : ` and ${middlemanName} places the production order with ${factoryName} for ${fmt(factoryUnitPrice!)}`}:`
    )
    lines.push("")

    // Pricing table
    lines.push("| Party | Role | Unit Price |")
    lines.push("|-------|------|-----------|")
    lines.push(`| ${importerName} | Importer (pays) | ${fmt(middlemanUnitPrice!)} |`)
    lines.push(`| ${middlemanName} | Middleman (receives/pays) | ${fmt(middlemanUnitPrice!)} / ${hasIntermediary && intermediaryUnitPrice != null ? fmt(intermediaryUnitPrice) : fmt(factoryUnitPrice!)} |`)
    if (hasIntermediary && intermediaryUnitPrice != null) {
      lines.push(`| ${intermediaryName} | Intermediary (receives/pays) | ${fmt(intermediaryUnitPrice)} / ${fmt(factoryUnitPrice!)} |`)
    }
    lines.push(`| ${factoryName} | Factory (receives) | ${fmt(factoryUnitPrice!)} |`)
    lines.push("")

    const dutyOnMiddleman = middlemanUnitPrice! * (dutyRate! / 100)
    const dutyOnFactory = factoryUnitPrice! * (dutyRate! / 100)
    const savings = dutyOnMiddleman - dutyOnFactory

    if (htsCode) {
      lines.push(
        `As of this date, the duty rate on ${productDescription || "these goods"}${htsCode ? ` (HTS ${htsCode})` : ""} is **${dutyRate}%**. Duty is currently being paid on the ${fmt(middlemanUnitPrice!)} that ${importerName} pays ${middlemanName}, the duty owed would be **${fmt(dutyOnMiddleman)}** (${dutyRate}% duty). If the "first sale" appraisement could be achieved, duty paid would be **${fmt(dutyOnFactory)}** (${fmt(factoryUnitPrice!)} × ${dutyRate}% duty).`
      )
    } else {
      lines.push(
        `At a duty rate of **${dutyRate}%**, duty is currently being paid on the ${fmt(middlemanUnitPrice!)} that ${importerName} pays ${middlemanName}. The duty owed would be **${fmt(dutyOnMiddleman)}** (${dutyRate}% duty). If the "first sale" appraisement could be achieved, duty paid would be **${fmt(dutyOnFactory)}** (${fmt(factoryUnitPrice!)} × ${dutyRate}% duty).`
      )
    }
    lines.push("")

    // Savings summary table
    lines.push("| Scenario | Dutiable Value | Duty Owed | Savings |")
    lines.push("|----------|---------------|-----------|---------|")
    lines.push(`| Current (middleman price) | ${fmt(middlemanUnitPrice!)} | ${fmt(dutyOnMiddleman)} | — |`)
    lines.push(`| First Sale (factory price) | ${fmt(factoryUnitPrice!)} | ${fmt(dutyOnFactory)} | **${fmt(savings)}** per unit |`)
    lines.push("")
  }

  // ── Documentation Requirements ──────────────────────────────────
  lines.push("## Documentation Requirements")
  lines.push("")
  lines.push(
    "Products made overseas for U.S. importers are usually built to U.S. specifications, which are often detailed in purchase orders, contracts, or sales confirmations. These documents are part of normal business practices and often help support a first sale claim. Each part of the transaction must be clearly documented to meet CBP requirements. The documentation below is required to support a first sale claim, and we are requesting one set of documents including the following:"
  )
  lines.push("")

  if (hasIntermediary) {
    lines.push(`1. Purchase order from ${importerName} to ${middlemanName}`)
    lines.push(`2. Purchase order from ${middlemanName} to ${intermediaryName}`)
    lines.push(`3. Purchase order from ${intermediaryName} to ${factoryName}`)
    lines.push(`4. Invoice from ${factoryName} to ${intermediaryName}`)
    lines.push(`5. Invoice from ${intermediaryName} to ${middlemanName}`)
    lines.push(`6. Invoice from ${middlemanName} to ${importerName}`)
    lines.push(`7. Proof of payment from ${importerName} to ${middlemanName}`)
    lines.push(`8. Proof of payment from ${middlemanName} to ${intermediaryName}`)
    lines.push(`9. Proof of payment from ${intermediaryName} to ${factoryName}`)
    lines.push(
      `10. Local transportation documents from ${factoryName} to ${intermediaryName}, including payment`
    )
  } else {
    lines.push(`1. Purchase order from ${importerName} to ${middlemanName}`)
    lines.push(`2. Purchase order from ${middlemanName} to ${factoryName}`)
    lines.push(`3. Invoice from ${factoryName} to ${middlemanName}`)
    lines.push(`4. Invoice from ${middlemanName} to ${importerName}`)
    lines.push(`5. Proof of payment from ${importerName} to ${middlemanName}`)
    lines.push(`6. Proof of payment from ${middlemanName} to ${factoryName}`)
  }
  lines.push(
    `${hasIntermediary ? "11" : "7"}. Export documentation from the country of origin indicating the ultimate destination is the United States (bills of lading, packing lists, etc.)`
  )
  lines.push(
    `${hasIntermediary ? "12" : "8"}. If ${factoryName} is provided with any materials free of charge or at a reduced cost (e.g., materials, designs, molds, patterns, etc.), invoices and payments for the materials`
  )
  lines.push(
    `${hasIntermediary ? "13" : "9"}. Port handling invoice and payment`
  )
  lines.push("")
  lines.push(
    "CBP will not require this level of detail for every transaction. While the listed documents are not required at the time of entry, it is recommended that the importer set up a recordkeeping program that requires the middleman to keep the necessary documents after importation to support the entered values for the goods."
  )
  lines.push("")

  // ── VAT Charges ─────────────────────────────────────────────────
  lines.push("## VAT Charges")
  lines.push("")
  lines.push(
    "If VAT is listed separately on the factory invoice and later refunded to the exporter, it can be excluded from the dutiable value. With proper documentation, CBP will allow the VAT to be removed from the declared value at entry."
  )
  lines.push("")
  lines.push(
    "To carve out VAT from dutiability, we need to review VAT documents maintained by all parties to ensure that the VAT charges were actually refunded, and that going forward, the companies receive and maintain proper supporting documentation."
  )
  lines.push("")

  // ── Remedies for Finally Liquidated Entries ────────────────────
  lines.push("## Remedies for Finally Liquidated Entries")
  lines.push("")
  lines.push(
    "Importers should be aware of the remedies available for entries that have already been finally liquidated. Under 19 U.S.C. § 1520(d), an importer may file a request for reliquidation within 180 days of the date of liquidation to claim a refund of excess duties paid. This provision allows importers to apply the first sale valuation retroactively to entries that were originally liquidated at the higher middleman price."
  )
  lines.push("")
  lines.push(
    "Additionally, 28 U.S.C. § 1581(a) provides jurisdiction to the U.S. Court of International Trade to review denied protests. Importers who have filed timely protests under 19 U.S.C. § 1514 that were denied by CBP may seek judicial review. The court has consistently upheld the first sale doctrine where importers have demonstrated compliance with the two-prong test established in *Nissho Iwai American Corp. v. United States* (982 F.2d 505, Fed. Cir. 1992)."
  )
  lines.push("")
  lines.push(
    "For prospective entries, the first sale valuation may be applied immediately once the requisite documentation is in place. CBP does not require advance approval, though importers should maintain complete records in the event of a post-entry audit."
  )
  lines.push("")

  // ── Conclusion ──────────────────────────────────────────────────
  lines.push("## Conclusion")
  lines.push("")
  lines.push(
    "The First Sale Program offers a valuable opportunity for importers to reduce customs duties, but it requires thorough compliance with legal and documentary requirements. By understanding the conditions and maintaining comprehensive records, importers can leverage this program effectively and minimize risk."
  )
  lines.push("")
  lines.push(
    "A comprehensive flow chart of the first sale documentation requirements and decision tree can be found in the appendix to this memorandum."
  )
  lines.push("")
  lines.push("Please feel free to contact our office with any questions.")

  return lines.join("\n")
}
